import { useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import PopularSearchesQuery from './PopularSearchesQuery';
import deconstructLocation from '../../../../../utils/deconstructLocation';
import { getNestedValue, isNotEmptyArray } from '../../utils/helpers';
import { CATEGORY_EXCLUSIONS } from '../../../../../constants';
import { Context } from '../../../../../context/AppContext';
import { MAX_SUGGESTIONS } from '../../../constants';

/**
 * Transforms fetched data object into an array of popular searches. Excluded categories are filtered out.
 * @param {Object} args.data The fetched data
 * @param {number} args.maxSuggestions The maximum number of suggestions to be returned
 * @returns {Array} Array of popular searches
 */
const transformData = ({ data, maxSuggestions }) => {
  const [first] = getNestedValue(data, 'searchSeoHome', 'data');
  const fetchResults = getNestedValue(first, 'attributes', 'categories');
  if (!isNotEmptyArray(fetchResults)) return [];
  return fetchResults
    .filter(item => !CATEGORY_EXCLUSIONS.includes(item.name))
    .slice(0, maxSuggestions)
    .map(({ name }) => ({
      id: name,
      name,
      suggestLabel: name,
    }));
};

/**
 * Logic for fetching and returning the most popular searches within a search area
 * @returns {Object} {
 * error,
 * fetchPopularSearches,
 * loading,
 * popularSearches,
 * setPopularSearches,
 * }
 */
const usePopularSearches = () => {
  const [fetch, { data, error, loading }] = useLazyQuery(PopularSearchesQuery);
  const [popularSearches, setPopularSearches] = useState();
  const {
    media: { mobile },
  } = useContext(Context);

  const maxSuggestions = mobile
    ? MAX_SUGGESTIONS.MOBILE.POPULAR_SEARCHES
    : MAX_SUGGESTIONS.DESKTOP.POPULAR_SEARCHES;

  /**
   * Fetch the most popular categories for a given search area from the server
   * @param {Object} args.searchArea The search area
   * @returns {void}
   */
  const fetchPopularSearches = ({ searchArea }) => {
    const { suggestLabel } = searchArea;
    const { suburb, state } = deconstructLocation(suggestLabel);
    const fetchParams = {
      variables: {
        params: {
          suburb,
          state,
        },
      },
    };
    fetch(fetchParams);
  };

  /**
   * Update the most popular categories whenever the fetched data is changed
   */
  useEffect(() => {
    if (!loading) {
      const hasSuggestions =
        isNotEmptyArray(getNestedValue(data, 'searchSeoHome', 'data')) &&
        isNotEmptyArray(
          getNestedValue(data.searchSeoHome.data[0], 'attributes', 'categories')
        );
      if (hasSuggestions) {
        setPopularSearches(transformData({ data, maxSuggestions }));
      } else {
        setPopularSearches();
      }
    }
  }, [data, loading]);

  return {
    error,
    fetchPopularSearches,
    loading,
    popularSearches,
    setPopularSearches,
  };
};

export default usePopularSearches;
